import { useCallback, useState, memo } from 'react';
import { useRouter } from 'next/router';
import { faSliders, faXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { Button } from 'react-bootstrap';
import Accordion from 'react-bootstrap/Accordion';
import Offcanvas from 'react-bootstrap/Offcanvas';
import CategoryFilterBreadcrumbs from 'components/CategoryFilterBreadcrumbs';
import CategoryFilterList from 'components/CategoryFilterList';
import Link from 'components/Link';
import { transformFacetName } from 'data/dutchie/transformations';
import cleanObject from 'utils/clean-object';
import parseFacetVisibility from 'utils/parse-facet-visibility';
import styles from './CategoryFiltersSmall.module.scss';

function CategoryFiltersSmall({
  category,
  closeoutText,
  customCss,
  facets,
  filterParam = '',
  filtersImage,
  path,
  promotion,
  searchParam = '',
  title,
}) {
  const router = useRouter();
  const [showFilters, setShowFilters] = useState(false);

  const handleClose = useCallback(() => setShowFilters(false), []);
  const handleOpen = useCallback(() => setShowFilters(true), []);

  const { hiddenFilterParams, sanitizedParams, visibleFilterCount } =
    parseFacetVisibility(facets, filterParam);

  let filterCnt = visibleFilterCount;

  if (searchParam) filterCnt++;
  if (promotion) filterCnt++;

  const filterText = filterCnt > 0 ? `Filters (${filterCnt})` : 'Filters';

  return (
    <div className={classNames('d-block d-md-none w-50 me-4', customCss)}>
      <button
        className={classNames(
          `d-flex flex-row bg-transparent border-2 border-primary justify-content-between px-6 py-3 text-primary text-start w-100`,
          styles.button
        )}
        type="button"
        onClick={handleOpen}
      >
        <span data-value="open-category-page-mobile-filter">{filterText}</span>
        {filtersImage ? (
          <span
            className={classNames(
              'd-inline-block position-relative',
              styles.filter_icon
            )}
          >
            <FontAwesomeIcon icon={faSliders} />
          </span>
        ) : null}
      </button>
      <Offcanvas
        show={showFilters}
        className={styles.filter_list}
        id="filter_drawer"
        name="filter_drawer"
        placement="start"
        onHide={handleClose}
      >
        <div className="w-100 bg-white" id="filter_menu_bottom">
          <div className="d-flex flex-row align-items-right justify-content-end w-100 p-5">
            <button
              type="button"
              className="border-0 bg-transparent font-sofia px-0"
              aria-label="Close"
              onClick={handleClose}
            >
              <FontAwesomeIcon
                color="primary"
                className={styles.icon}
                icon={faXmark}
              />
            </button>
          </div>
          <div className="ps-15 pe-10">
            <CategoryFilterBreadcrumbs
              category={category}
              className="border-bottom-0"
              facets={facets}
              filterParam={filterParam}
              path={path}
              promotion={promotion}
              searchParam={searchParam}
              showClearAll={false}
              title={title}
            />
          </div>
        </div>
        <div className={classNames('flex-grow-1', styles.accordion_wrap)}>
          <Accordion
            defaultActiveKey={facets.length === 1 ? '0' : undefined}
            className={classNames(styles.accordion)}
          >
            {facets.map((facet, idx) => {
              if (!facet || !facet.facet || !facet.value || !facet.visible) {
                return null;
              }

              const { ranges: unsanitizedRanges, terms: unsanitizedTerms } =
                facet.value;
              const ranges = unsanitizedRanges || [];
              const terms = (unsanitizedTerms || []).filter(({ term }) => term);

              if (ranges.length === 0 && terms.length === 0) {
                return null;
              }

              if (facet.facet === 'Brand' && terms.length < 2) {
                return null;
              }

              const { facet: facetName, value } = facet;
              return (
                <Accordion.Item
                  key={`${facetName}${path}`}
                  eventKey={idx.toString()}
                  data-value="category-page-mobile-accordion"
                >
                  <Accordion.Header data-value="category-page-mobile-accordion-header">
                    {transformFacetName(facetName)}
                  </Accordion.Header>
                  <Accordion.Body>
                    <CategoryFilterList
                      data-value={facetName}
                      className="mt-0 mb-0"
                      closeoutText={closeoutText}
                      facet={facetName}
                      filterParam={sanitizedParams.join(',')}
                      filterType={value.type}
                      path={path}
                      promotion={promotion}
                      ranges={value.ranges}
                      terms={value.terms}
                    />
                  </Accordion.Body>
                </Accordion.Item>
              );
            })}
          </Accordion>
        </div>
        <div className="w-100 d-flex flex-row text-center border-0 border-top border-primary py-15 px-10">
          <div className={classNames('w-50 px-5', styles.clear_all)}>
            <Link
              shallow
              href={{
                pathname: path,
                query: cleanObject({
                  sort: router.query.sort,
                  ...(hiddenFilterParams && {
                    filters: hiddenFilterParams.join(','),
                  }),
                }),
              }}
              className={classNames(
                'btn border border-1 text-uppercase border-primary text-primary w-100 font-sofia fs-8 fw-700 mb-0',
                styles.clear_all
              )}
              data-value="clear-all-filters"
              onClick={handleClose}
            >
              Clear All
            </Link>
          </div>
          <div className="w-50 px-5">
            <Button
              className="border border-1 border-primary font-sofia fw-700 text-uppercase w-100 h-100"
              type="button"
              variant="primary"
              data-value="close-category-page-mobile-filter"
              onClick={handleClose}
            >
              Done
            </Button>
          </div>
        </div>
      </Offcanvas>
    </div>
  );
}

export default memo(CategoryFiltersSmall);
